import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterLink } from '@angular/router';
import { User } from '../../core/models/user.model';
import { DoctorReportSummary } from '../../core/models/doctor-report-summary';
import { NotificationService } from '../../core/services/notification.service';
import { UserManagementService } from '../../core/services/user-management.service';
import { UserManagementDto } from '../../core/models/user-management.dto';
import { UserRole } from '../../core/enums/user-role.enum';
import { PortalNavigation, PortalNavigationItem } from '../portal-navigation/portal-navigation';
import { BookingService } from '../../core/services/booking.service';
import { ClinicStatusService } from '../../core/services/clinic-status.service';
import { ClinicStatus } from '../../core/enums/clinic-status.enum';
import { ClinicMembership } from '../../core/models/clinic-membership.model';
import { DoctorProfileService } from '../../core/services/doctor-profile.service';
import { SettingsService } from '../../core/services/settings.service';
import { SubscriptionPaymentStatus } from '../../core/models/subscription-payment.model';

import { TermsAndConditionsModal } from '../terms-and-conditions-modal/terms-and-conditions-modal';
import { ContactUsModal } from '../contact-us-modal/contact-us-modal';

@Component({
  selector: 'app-manager-dashboard',
  standalone: true,
  imports: [CommonModule, RouterLink, PortalNavigation, TermsAndConditionsModal, ContactUsModal],
  templateUrl: './manager-dashboard.html',
  styleUrl: './manager-dashboard.scss',
})
export class ManagerDashboard implements OnInit {
  isLoading = true;
  isClinicActive = false;
  clinicStatus: ClinicStatus | null = null;
  clinicSuspensionComments: string | null = null;
  userProfile: User | null = null;
  reportSummary: DoctorReportSummary | undefined;
  users: UserManagementDto[] = [];
  usersLoading = false;
  isResettingPasswordId: string | null = null;
  isTogglingStatusId: string | null = null;
  clinicMemberships: ClinicMembership[] = [];
  usersWithExpiringPasswords: UserManagementDto[] = [];
  unlinkedReceptionists: UserManagementDto[] = [];
  subscriptionPaymentStatus: SubscriptionPaymentStatus | null = null;

  get profileNavigationItems(): PortalNavigationItem[] {
    return [
      { label: 'Reporting', icon: 'bi-bar-chart-line', route: '/dashboard/reporting' },
      {
        label: 'User Management',
        icon: 'bi-people',
        route: '/dashboard/manage-users',
        disabled: !this.isClinicActive,
      },
    ];
  }

  constructor(
    private router: Router,
    private userManagementService: UserManagementService,
    private notificationService: NotificationService,
    private clinicStatusService: ClinicStatusService,
    private bookingService: BookingService,
    private profileService: DoctorProfileService,
    private settingsService: SettingsService,
  ) {
    this.userProfile = history.state['managerData'] ?? this.loadStoredUserProfile();
  }

  ngOnInit(): void {
    this.isLoading = false;
    this.loadUsers();
    this.loadSubscriptionStatus();
    this.loadReportSummary();
    this.clinicStatusService.load();
    this.clinicStatusService.isClinicActive$.subscribe((active) => {
      this.isClinicActive = active;
    });
    this.clinicStatusService.status$.subscribe((status) => {
      this.clinicStatus = status;
    });
    this.clinicStatusService.suspensionComments$.subscribe((comments) => {
      this.clinicSuspensionComments = comments;
    });
    this.loadClinicMemberships();
  }

  private loadSubscriptionStatus(): void {
    this.settingsService.getClinicSubscriptionPaymentStatus().subscribe({
      next: (status) => (this.subscriptionPaymentStatus = status),
      error: () => (this.subscriptionPaymentStatus = null),
    });
  }

  get isTrialActive(): boolean {
    return this.subscriptionPaymentStatus?.trialActive === true;
  }

  get hasLatestInvoiceFailed(): boolean {
    return this.subscriptionPaymentStatus?.latestInvoiceFailed === true;
  }

  private loadClinicMemberships(): void {
    this.userManagementService.getClinicMemberships().subscribe({
      next: (memberships) => {
        this.clinicMemberships = memberships;
      },
      error: () => {
        this.clinicMemberships = [];
      },
    });
  }

  onSwitchClinic(clinicId: string): void {
    this.profileService.switchClinicContext(clinicId);
  }

  loadReportSummary(): void {
    this.bookingService.getReportSummary(null).subscribe({
      next: (res) => {
        this.reportSummary = res;
      },
      error: (error) => {
        this.notificationService.error(
          'Failed to load report summary: ' +
            (error?.error?.message || error.message || 'Unknown error'),
        );
      },
    });
  }

  loadUsers(): void {
    this.usersLoading = true;
    this.userManagementService.getClinicUsers().subscribe({
      next: (users) => {
        this.users = users;
        this.usersWithExpiringPasswords = this.getUsersWithExpiringPasswords(users);
        this.unlinkedReceptionists = this.getUnlinkedReceptionists(users);
        this.usersLoading = false;
      },
      error: (error) => {
        this.notificationService.error(
          'Failed to load practice users: ' +
            (error?.error?.message || error.message || 'Unknown error'),
        );
        this.usersLoading = false;
      },
    });
  }

  private getUsersWithExpiringPasswords(users: UserManagementDto[]): UserManagementDto[] {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    return users
      .filter((user) => {
        if (!user.passwordExpiresAt) return false;

        const expirationDate = new Date(user.passwordExpiresAt);
        expirationDate.setHours(0, 0, 0, 0);

        const daysUntilExpiry = Math.ceil(
          (expirationDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24),
        );

        // Show users with passwords expiring in 14 days or less (but not already expired)
        return daysUntilExpiry <= 14 && daysUntilExpiry > 0;
      })
      .sort((a, b) => {
        const dateA = new Date(a.passwordExpiresAt ?? 0).getTime();
        const dateB = new Date(b.passwordExpiresAt ?? 0).getTime();
        return dateA - dateB;
      });
  }

  private getUnlinkedReceptionists(users: UserManagementDto[]): UserManagementDto[] {
    return users.filter(
      (user) => user.role === 'RECEPTION' && !user.clinicMembershipId && user.active,
    );
  }

  getDaysUntilExpiry(date: Date | undefined): number | null {
    if (!date) return null;

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const expirationDate = new Date(date);
    expirationDate.setHours(0, 0, 0, 0);

    return Math.ceil((expirationDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
  }

  resetPassword(user: UserManagementDto): void {
    this.isResettingPasswordId = user.id;

    this.userManagementService.resetPassword(user.id).subscribe({
      next: (result) => {
        this.isResettingPasswordId = null;
        if (result) {
          this.notificationService.success('Password reset email sent successfully.');
        } else {
          this.notificationService.error('Password reset request could not be completed.');
        }
      },
      error: (error) => {
        this.isResettingPasswordId = null;
        this.notificationService.error(
          'Failed to reset password: ' +
            (error?.error?.message || error.message || 'Unknown error'),
        );
      },
    });
  }

  toggleUserStatus(user: UserManagementDto): void {
    if (this.isDoctor(user)) {
      this.toggleDoctorStatus(user);
      return;
    }
    this.isTogglingStatusId = user.id;

    this.userManagementService.toggleUserStatus(user.id).subscribe({
      next: (updatedUser) => {
        this.isTogglingStatusId = null;
        this.notificationService.success(
          `User ${updatedUser.active ? 'activated' : 'deactivated'} successfully.`,
        );
        this.loadUsers();
      },
      error: (error) => {
        this.isTogglingStatusId = null;
        this.notificationService.error(
          'Failed to update user status: ' +
            (error?.error?.message || error.message || 'Unknown error'),
        );
      },
    });
  }

  private toggleDoctorStatus(user: UserManagementDto): void {
    if (!user.clinicMembershipId) {
      this.notificationService.error('Cannot deactivate doctor: membership ID missing.');
      return;
    }

    this.isTogglingStatusId = user.id;
    this.userManagementService.toggleDoctorMembershipStatus(user.clinicMembershipId).subscribe({
      next: (updatedUser) => {
        this.isTogglingStatusId = null;
        this.notificationService.success(
          'Doctor ' + (updatedUser.active ? 'activated' : 'deactivated') + ' in this practice.',
        );
        this.loadUsers();
      },
      error: (error) => {
        this.isTogglingStatusId = null;
        this.notificationService.error(
          'Failed to update doctor status: ' +
            (error?.error?.message || error.message || 'Unknown error'),
        );
      },
    });
  }

  canToggleUserStatus(user: UserManagementDto): boolean {
    if (this.isCurrentUser(user)) {
      return false;
    }
    if (!this.isManagerUser(user)) {
      return true;
    }

    return this.managerCount > 1;
  }

  isReceptionist(user: UserManagementDto): boolean {
    return user.role === 'RECEPTION';
  }

  isManagerUser(user: UserManagementDto): boolean {
    return user.role?.toUpperCase() === UserRole.MANAGER;
  }

  isDoctor(user: UserManagementDto): boolean {
    return user.role?.toUpperCase() === UserRole.DOCTOR;
  }

  private isCurrentUser(user: UserManagementDto): boolean {
    if (!this.userProfile) {
      return false;
    }
    return this.userProfile.id === user.id || this.userProfile.email === user.email;
  }

  get managerCount(): number {
    return this.users.filter((user) => this.isManagerUser(user)).length;
  }

  // ── Navigation ───────────────────────────────────────────────────────────

  navigateToOnboardUsers(): void {
    this.router.navigate(['dashboard/onboard-users']);
  }

  navigateToInviteDoctor(): void {
    if (this.isTrialActive) {
      return;
    }
    this.router.navigate(['dashboard/invite-doctor']);
  }

  navigateToReports(): void {
    this.router.navigate(['dashboard/reporting']);
  }

  navigateToBilling(): void {
    this.router.navigate(['dashboard/billing-summary']);
  }

  navigateToManageUsers(): void {
    this.router.navigate(['dashboard/manage-users']);
  }

  navigateToViewPatients(): void {
    this.router.navigate(['dashboard/patients-view']);
  }

  navigateToProfile(): void {
    this.router.navigate(['/dashboard/profile']);
  }

  navigateToSettings(): void {
    this.router.navigate(['/dashboard/settings']);
  }

  get clinicStatusDisplay(): { label: string; icon: string; pillClass: string } {
    switch (this.clinicStatus) {
      case ClinicStatus.ACTIVE:
        return { label: 'Practice Active', icon: 'bi-shield-check', pillClass: 'status-confirmed' };
      case ClinicStatus.REVIEW_PENDING:
        return { label: 'Review Pending', icon: 'bi-clock-history', pillClass: 'status-pending' };
      case ClinicStatus.SUSPENDED:
        return { label: 'Practice Suspended', icon: 'bi-shield-x', pillClass: 'status-cancelled' };
      case ClinicStatus.INACTIVE:
        return {
          label: 'Practice Inactive',
          icon: 'bi-dash-circle',
          pillClass: 'status-cancelled',
        };
      default:
        return { label: 'Review Pending', icon: 'bi-clock-history', pillClass: 'status-pending' };
    }
  }

  private loadStoredUserProfile(): User | null {
    const storedProfile = localStorage.getItem('userProfile');

    if (!storedProfile) {
      return null;
    }

    try {
      return JSON.parse(storedProfile) as User;
    } catch {
      return null;
    }
  }
}
